import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";
import { cva, VariantProps } from "class-variance-authority";
import { Img } from "./img";

const tooltipContentVariants = cva("", {
  variants: {
    size: {
      text: "py-0.5 px-1.5",
      screen: "px-6.5 py-3.75",
    },
    variant: {
      default: "",
      outlined: "border-3 border-primary-light ",
      btnTip: "bg-gray min-w-18.75 text-center fs-13 rounded-[5px] text-white",
    },
  },
  defaultVariants: {
    size: "screen",
    variant: "default",
  },
});

const tooltipArrowVariants = cva("", {
  variants: {
    size: {
      text: "rounded-none",
      screen: "",
    },
    variant: {
      default: "",
      outlined: "bg-primary-light fill-primary-light",
      btnTip: "bg-gray fill-gray",
    },
  },
});

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

type TooltipProps = React.ComponentProps<typeof TooltipPrimitive.Root>;

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

interface TooltipContentProps
  extends React.ComponentProps<typeof TooltipPrimitive.Content>,
    VariantProps<typeof tooltipContentVariants> {
  portal?: HTMLElement | null;
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  variant,
  portal,
  size,
  ...props
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal container={portal}>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        {...props}
        className={cn(
          "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-0.5 data-[side=left]:slide-in-from-right-0.5 data-[side=right]:slide-in-from-left-0.5 data-[side=top]:slide-in-from-bottom-0.5 z-50 w-fit origin-(--radix-tooltip-content-transform-origin)",
        )}
      >
        <div
          className={cn(
            "bg-paper text-foreground relative z-1 rounded text-xs text-balance **:text-current",
            tooltipContentVariants({ variant, size }),
            className,
          )}
        >
          {children}
        </div>
        <TooltipPrimitive.Arrow
          className={cn(
            "bg-primary fill-primary size-4 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[3px]",
            tooltipArrowVariants({ variant, size }),
          )}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

type ScreenTooltipProps = Omit<TooltipProps, "arrow"> & {
  image: string;
} & VariantProps<typeof tooltipContentVariants>;

function ScreenTooltip({ image, variant, children, ...props }: ScreenTooltipProps) {
  return (
    <Tooltip {...props}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent portal={document.getElementById("tooltip-backdrop")} variant={variant}>
        <Img src={image} alt="Tooltip Image" className="aspect-[197/398] w-50" />
      </TooltipContent>
    </Tooltip>
  );
}

export {
  ScreenTooltip,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipPrimitive,
};
